import React from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircle, Copy, Check } from 'lucide-react'
import { Card, CardContent } from '../ui/card'
import { Button } from '../ui/button'

interface ErrorPageProps {
  errorId?: string
  // 기술 상세 토글에 쓰일 원본 에러 (development 모드에서만 노출)
  error?: unknown
  onRetry?: () => void
  onHome?: () => void
}

/**
 * Level 1 — 앱 전역 크래시 페이지.
 * `ErrorBoundaryWrapper`가 React 렌더 에러를 잡았을 때 렌더된다.
 *
 * 사용자에게는 기술 메시지 대신 정돈된 카피와 Error ID(CS 문의용)를 보여주고,
 * development 모드에서만 원본 스택을 토글로 노출한다.
 */
export const ErrorPage: React.FC<ErrorPageProps> = ({
  errorId,
  error,
  onRetry,
  onHome,
}) => {
  const { t } = useTranslation('common')
  const [copied, setCopied] = React.useState(false)
  const [showDetails, setShowDetails] = React.useState(false)

  const isDev = process.env.NODE_ENV === 'development'

  const handleCopy = async () => {
    if (!errorId) return
    try {
      await navigator.clipboard.writeText(errorId)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    } catch {
      // 클립보드 API 실패는 무시 — 에러 페이지에서 추가 에러 발생 방지
    }
  }

  const handleRetry = onRetry ?? (() => window.location.reload())
  const handleHome = onHome ?? (() => (window.location.href = '/'))

  const technicalMessage = React.useMemo(() => {
    if (!error) return ''
    if (error instanceof Error)
      return `${error.name}: ${error.message}\n${error.stack ?? ''}`
    try {
      return JSON.stringify(error, null, 2)
    } catch {
      return String(error)
    }
  }, [error])

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <Card className="w-full max-w-lg">
        <CardContent className="flex flex-col items-center text-center space-y-6 py-10">
          <AlertCircle className="w-14 h-14 text-destructive" />

          <div className="space-y-2">
            <h1 className="text-xl font-semibold">
              {t('errorPage.title', '일시적인 오류가 발생했습니다')}
            </h1>
            <p className="text-muted-foreground text-sm max-w-sm">
              {t(
                'errorPage.description',
                '문제가 지속되면 아래 에러 ID와 함께 고객센터로 문의해주세요.'
              )}
            </p>
          </div>

          {errorId && (
            <div className="w-full rounded-md border bg-muted/40 px-3 py-2 flex items-center justify-between gap-2">
              <div className="text-left">
                <div className="text-xs text-muted-foreground">
                  {t('errorPage.errorIdLabel', '에러 ID')}
                </div>
                <div className="font-mono text-sm break-all">{errorId}</div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopy}
                aria-label={t('errorPage.copyErrorId', '에러 ID 복사')}
              >
                {copied ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </Button>
            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap justify-center">
            <Button onClick={handleRetry} variant="default">
              {t('errorPage.retry', '다시 시도')}
            </Button>
            <Button onClick={handleHome} variant="outline">
              {t('errorPage.goHome', '홈으로')}
            </Button>
          </div>

          {isDev && error != null && (
            <div className="w-full text-left">
              <button
                type="button"
                className="text-xs text-muted-foreground underline"
                onClick={() => setShowDetails((v) => !v)}
              >
                {t('errorPage.technicalDetails', '기술 상세')}
              </button>
              {showDetails && (
                <pre className="mt-2 max-h-60 overflow-auto text-xs bg-muted/40 rounded p-2 whitespace-pre-wrap">
                  {technicalMessage}
                </pre>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
